import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2, ShieldCheck, Clock, Ticket, CreditCard, Sparkles } from "lucide-react";

const fmtIDR = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

export function SubscribeModal({ analyst, onClose, onSubscribed }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("options"); // options | qr | paid_done | wl_done
  const [order, setOrder] = useState(null);
  const [qrStatus, setQrStatus] = useState("idle");
  const [code, setCode] = useState("");
  const [wlStatus, setWlStatus] = useState(analyst.whitelist_status || "none");
  const timer = useRef(null);

  const needAuth = () => { if (!user) { navigate("/login"); return true; } return false; };

  const requestWhitelist = async () => {
    if (needAuth()) return;
    try {
      const { data } = await client.post(`/analysts/${analyst.id}/whitelist`);
      setWlStatus(data.status); setView("wl_done");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send request."); }
  };

  const redeem = async () => {
    if (needAuth()) return;
    if (!code.trim()) return;
    try {
      await client.post("/subscriptions/redeem", { code });
      toast.success("Access unlocked!"); onSubscribed?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code."); }
  };

  const startQris = async () => {
    if (needAuth()) return;
    setView("qr"); setQrStatus("loading");
    try {
      const { data } = await client.post("/subscriptions/qris", { analyst_id: analyst.id });
      setOrder(data); setQrStatus("qr");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setQrStatus("error");
      setOrder({ error: typeof detail === "string" ? detail : "Could not start the payment. Please try again shortly." });
    }
  };

  useEffect(() => {
    if (qrStatus !== "qr" || !order?.order_id) return;
    timer.current = setInterval(async () => {
      try {
        const { data } = await client.get(`/subscriptions/order/${order.order_id}`);
        if (data.status === "PAID") { clearInterval(timer.current); setView("paid_done"); onSubscribed?.(); }
        else if (data.status === "FAILED") { clearInterval(timer.current); setQrStatus("error"); setOrder({ error: "Payment failed or expired." }); }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(timer.current);
  }, [qrStatus, order, onSubscribed]);

  const paidOn = analyst.paid_enabled && analyst.subscription_price > 0;
  const wlOn = analyst.whitelist_enabled;

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm p-4" data-testid="subscribe-modal">
      <div className="cl-card w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto" style={{ transform: "none" }}>
        <button onClick={onClose} data-testid="subscribe-close" className="absolute top-4 right-4 grid place-items-center w-8 h-8 rounded-full text-zinc-400 hover:text-white hover:bg-white/5"><X className="w-4 h-4" /></button>
        <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-[#ccff00] mb-1">Get Access</p>
        <h3 className="font-display text-2xl font-bold">{analyst.name}</h3>
        <p className="text-sm text-zinc-400 mt-1">{analyst.tagline}</p>

        {view === "options" && (
          <div className="mt-5 space-y-3">
            {/* Whitelist */}
            {wlOn && (
              wlStatus === "pending" ? (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.05] p-4">
                  <p className="flex items-center gap-2 text-sm text-amber-300"><Clock className="w-4 h-4" /> Your whitelist request is pending approval.</p>
                </div>
              ) : (
                <div className="rounded-xl border border-[#ccff00]/25 bg-[#ccff00]/[0.04] p-4">
                  <p className="flex items-center gap-2 font-semibold text-sm"><Sparkles className="w-4 h-4 text-[#ccff00]" /> Request Whitelist Access</p>
                  <p className="text-xs text-zinc-400 mt-1">Exclusive, invite-style access. The analyst reviews and approves your request.</p>
                  <button onClick={requestWhitelist} data-testid="request-whitelist-btn" className="mt-3 w-full py-2.5 rounded-full bg-[#ccff00] text-black font-semibold text-sm hover:brightness-110 transition-[filter]">Request Access</button>
                </div>
              )
            )}
            {/* Paid */}
            {paidOn && (
              <div className="rounded-xl border border-white/10 p-4">
                <div className="flex items-end justify-between">
                  <p className="flex items-center gap-2 font-semibold text-sm"><CreditCard className="w-4 h-4 text-[#ccff00]" /> Paid Subscription</p>
                  <p className="font-display text-lg font-black text-[#ccff00]">{fmtIDR(analyst.subscription_price)}<span className="text-[11px] text-zinc-500 font-mono-cl"> / {analyst.subscription_days || 30}d</span></p>
                </div>
                <button onClick={startQris} data-testid="subscribe-pay-qris" className="mt-3 w-full py-2.5 rounded-full bg-[#ccff00] text-black font-semibold text-sm hover:brightness-110 transition-[filter]">Pay with QRIS</button>
              </div>
            )}
            {/* Redeem code */}
            <div className="rounded-xl border border-white/10 p-4">
              <p className="flex items-center gap-2 font-semibold text-sm"><Ticket className="w-4 h-4 text-[#ccff00]" /> Have an access code?</p>
              <div className="flex gap-2 mt-3">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="CL-XXXXXXXX" data-testid="redeem-input"
                  className="flex-1 bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#ccff00]/40 uppercase" />
                <button onClick={redeem} data-testid="redeem-btn" className="shrink-0 px-5 py-2 rounded-full border border-[#ccff00]/40 text-[#ccff00] text-sm font-semibold hover:bg-[#ccff00]/10">Redeem</button>
              </div>
            </div>
            {!wlOn && !paidOn && (
              <p className="text-sm text-zinc-500 text-center py-2">Subscriptions are not open yet. You can still redeem an access code above.</p>
            )}
          </div>
        )}

        {view === "qr" && (
          <div className="mt-5 text-center">
            {qrStatus === "loading" && <div className="py-8 flex items-center justify-center gap-2 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /> Generating QR…</div>}
            {qrStatus === "qr" && order?.qr_string && (
              <>
                <div className="bg-white rounded-2xl p-4 inline-block"><QRCodeSVG value={order.qr_string} size={220} /></div>
                <p className="text-sm text-zinc-300 mt-3">Scan with any QRIS app (GoPay, OVO, DANA, ShopeePay, bank apps).</p>
                <p className="font-mono-cl text-[11px] text-zinc-500 mt-1 flex items-center justify-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Waiting for payment…</p>
              </>
            )}
            {qrStatus === "error" && (
              <div className="py-6"><p className="text-sm text-zinc-300">{order?.error}</p>
                <button onClick={() => setView("options")} className="mt-4 px-5 py-2.5 rounded-full border border-white/15 text-sm">Back</button></div>
            )}
          </div>
        )}

        {view === "wl_done" && (
          <div className="mt-6 text-center py-4">
            <Clock className="w-12 h-12 text-amber-300 mx-auto" />
            <p className="font-display text-xl font-bold mt-3">Request sent</p>
            <p className="text-sm text-zinc-400 mt-1">You're on {analyst.name}'s whitelist queue. You'll get access once approved.</p>
            <button onClick={onClose} className="mt-5 w-full py-3 rounded-full bg-[#ccff00] text-black font-semibold">Done</button>
          </div>
        )}

        {view === "paid_done" && (
          <div className="mt-6 text-center py-4">
            <CheckCircle2 className="w-12 h-12 text-[#ccff00] mx-auto" />
            <p className="font-display text-xl font-bold mt-3">Subscription active</p>
            <p className="text-sm text-zinc-400 mt-1">You now have exclusive access to {analyst.name}.</p>
            <button onClick={onClose} className="mt-5 w-full py-3 rounded-full bg-[#ccff00] text-black font-semibold">Done</button>
          </div>
        )}

        <p className="mt-5 flex items-center gap-1.5 text-[11px] text-zinc-600 font-mono-cl"><ShieldCheck className="w-3.5 h-3.5" /> Secured by Midtrans · QRIS</p>
      </div>
    </div>
  );
}
